/**
 * 素材パネルからのスクロール動画 (docs/features/swipe-motion-clip-plan.md §11.4)。
 * ジョブを投入して生成キュー (activeJobs) で追跡し、完了で個人フォルダの一覧を更新してトーストを出す。
 * 選択中の素材への自動追加はしない (2026-10-14 に撤去: 動画を受け付けない画面では空振りか確認モーダルの割り込みになり、
 * バックグラウンド完了時に選択状態を勝手に変えるのも不意打ちになるため。使うときは素材パネルから選ぶ)。
 * YouTube アップロード (features/youtube/uploadJob.ts) と同じ作り (シーンには依らない)。
 */
import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';

import type { ActiveJob } from '../../components/generation/state/types';
import { waitForJobCompletion } from '../../services/asyncJobApi';
import { TEMP_QUERY_KEY } from '../materials/hooks';
import { createSwipeClipJob, type SwipeClipJobResult } from './api';
import type { SwipeClipParams } from './schema';
import { buildSwipePreview } from './renderInput';
import { swipeDurationSec } from './timeline';

interface ToastLike {
  show: (m: string) => void;
  success: (m: string) => void;
  error: (m: string) => void;
}

export interface StandaloneSwipeClipDeps {
  setActiveJobs: Dispatch<SetStateAction<ActiveJob[]>>;
  setShowJobQueueModal: (v: boolean) => void;
  toast: ToastLike;
  queryClient: QueryClient;
}

export interface StandaloneSwipeClipOptions {
  params: SwipeClipParams;
  /** キュー表示用の素材名。 */
  label: string;
}

/**
 * ジョブを投入し、キューに載せる。投入のエラーはモーダルに表示させるため投げる。追跡は裏で続ける。
 */
export async function startStandaloneSwipeClipJob(deps: StandaloneSwipeClipDeps, opts: StandaloneSwipeClipOptions): Promise<void> {
  const { job_id } = await createSwipeClipJob({ params: opts.params });
  let durationSeconds: number | undefined;
  try {
    durationSeconds = swipeDurationSec(buildSwipePreview(opts.params).timeline);
  } catch {
    durationSeconds = undefined;
  }
  deps.setActiveJobs((prev) => [...prev, {
    jobId: job_id,
    status: 'polling' as const,
    waiting: false,
    startedAt: Date.now(),
    type: 'video' as const,
    label: `${opts.label} (スクロール動画)`,
    durationSeconds,
  }]);
  deps.setShowJobQueueModal(true);
  void trackStandaloneSwipeClipJob(deps, job_id);
}

/** 完了まで追跡し、キューと個人フォルダの一覧に反映する。 */
export async function trackStandaloneSwipeClipJob(deps: StandaloneSwipeClipDeps, jobId: string): Promise<void> {
  const { setActiveJobs, toast, queryClient } = deps;
  try {
    const job = await waitForJobCompletion<SwipeClipJobResult>(jobId, {
      skipVisibilityCheck: true,
      onProgress: (info) => {
        setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId && j.status === 'polling' ? { ...j, waiting: info.status === 'pending' } : j)));
      },
    });
    const materialId = job.result_data?.material_id ?? null;
    setActiveJobs((prev) => prev.map((j) => (
      j.jobId === jobId
        ? { ...j, status: 'completed' as const, waiting: false, result: { id: materialId ?? jobId, ...(materialId ? { material_id: materialId } : {}) } }
        : j
    )));
    void queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
    if (!materialId) {
      toast.show('スクロール動画を個人フォルダに保存しましたが、素材の情報を取得できませんでした');
      return;
    }
    toast.success('スクロール動画を個人フォルダに保存しました');
  } catch (error) {
    console.error('Scroll video job failed:', error);
    const message = error instanceof Error ? error.message : 'スクロール動画の生成に失敗しました';
    setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'failed' as const, waiting: false, error: message } : j)));
    toast.error(message);
  }
}
